import React from 'react';
import { get } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { ActionButton } from 'routes/styles';
import Spacer from 'shared/components/Spacer';
import {
  ConditionSetType,
  ContactFormType,
  SegmentType,
  UserType,
} from '../../../../../shared/types';
import Icon from '../../../../../shared/components/Icon';
import SwitchButton from '../../../../../shared/components/SwitchButton';
import { ErrorField } from '../../../../../shared/styles';
import { ConditionButton, SegmentRuleContainer, TypeAction } from './styles';
import { matchTypes } from './constants';
import SegmentConditionSet from './SegmentConditionSet';

type SegmentRuleProps = {
  form: UseFormReturn<SegmentType>;
  contactForm: ContactFormType;
  disabled?: boolean;
  userInfo?: UserType;
};

const SegmentRule = (props: SegmentRuleProps) => {
  const { t } = useTranslation();
  const { form, contactForm, disabled, userInfo } = props;
  const {
    register,
    watch,
    setValue,
    formState: { errors, submitCount },
  } = form;
  const condition_sets = watch('rule.condition_sets');

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };
  const onDeleteConditionSet = (id: string) => {
    if (disabled) return;
    const newValue = condition_sets.filter(
      (conditionSet: ConditionSetType) => conditionSet.id !== id,
    );
    setFormValue(
      'rule.condition_sets',
      newValue.length === 0
        ? [
            {
              match_type: matchTypes[0].id,
              conditions: [
                {
                  id: uuid(),
                  name: '',
                  evaluate_on: 'CONTACT',
                  operator: '',
                  value: '',
                  field_type: '',
                },
              ],
            },
          ]
        : newValue,
    );
  };

  const onAddConditionSet = () => {
    if (disabled) return;
    const newValue = [...condition_sets];
    newValue.push({
      id: uuid(),
      match_type: matchTypes[0].id,
      conditions: [],
    });
    setFormValue('rule.condition_sets', newValue);
  };

  const onChangeConditionSet = (newConditionSet: ConditionSetType) => {
    const newValue: ConditionSetType[] = condition_sets.map((conditionSet) =>
      conditionSet.id === newConditionSet.id ? newConditionSet : conditionSet,
    );
    setFormValue('rule.condition_sets', newValue);
  };

  React.useEffect(() => {
    register('rule.condition_sets');
  }, []);

  return (
    <SegmentRuleContainer>
      <div className="custom-label">
        {t('segments.new_segment.segment_conditions')}
      </div>

      <TypeAction>
        <Trans
          i18nKey="segments.new_segment.include_contact_condition_group"
          components={{
            switchButton: (
              <SwitchButton
                {...register('rule.match_type')}
                value={watch('rule.match_type')}
                disabled={disabled}
                onChange={(item) => setFormValue('rule.match_type', item.id)}
                items={matchTypes}
                itemLabelIdentifier="name"
                itemValueIdentifier="id"
              />
            ),
          }}
        />
      </TypeAction>
      {condition_sets &&
        condition_sets.length > 0 &&
        condition_sets.map((conditionSet: ConditionSetType, index: number) => (
          <>
            <SegmentConditionSet
              form={form}
              key={conditionSet.id}
              errors={get(errors, ['rule', 'condition_sets', index], {})}
              contactForm={contactForm}
              onDelete={onDeleteConditionSet}
              actionsDisabled={condition_sets.length <= 1}
              disabled={disabled}
              onChange={onChangeConditionSet}
              conditionSet={conditionSet}
              userInfo={userInfo}
            />
            {index < condition_sets.length - 1 && (
              <ConditionButton>
                {watch('rule.match_type') === 'ALL' ? 'AND' : 'OR'}
              </ConditionButton>
            )}
          </>
        ))}
      <Spacer y={0.5} />
      <ActionButton
        onClick={onAddConditionSet}
        className={disabled ? 'disabled' : ''}
      >
        <div>
          <Icon width="12" height="12" glyph="plus" />
        </div>
        <div>{t('segments.new_segment.new_group')}</div>
      </ActionButton>
      {submitCount > 0 &&
        get(errors, ['rule', 'condition_sets', 'message'], false) && (
          <ErrorField>
            {get(errors, ['rule', 'condition_sets', 'message'], '')}
          </ErrorField>
        )}
    </SegmentRuleContainer>
  );
};

export default SegmentRule;
